import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import { getFocusClasses } from '../../utils/accessibility';
import type { BaseComponentProps } from '../../types';

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'>,
    BaseComponentProps {
  /**
   * Label del checkbox
   */
  label?: string;
  /**
   * Testo di descrizione
   */
  description?: string;
  /**
   * Stato indeterminato
   */
  indeterminate?: boolean;
  /**
   * Dimensione
   */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Componente Checkbox
 * 
 * Checkbox styled con supporto per label, descrizione e stato indeterminato.
 * Completamente accessibile.
 * 
 * @example
 * ```tsx
 * <Checkbox label="Accetta i termini" />
 * <Checkbox label="Newsletter" description="Ricevi aggiornamenti via email" />
 * <Checkbox indeterminate label="Seleziona tutti" />
 * ```
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      label,
      description,
      indeterminate,
      size = 'md',
      id,
      ...props
    },
    ref
  ) => {
    const sizeClasses = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6',
    };

    const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

    const checkboxClasses = cn(
      'rounded border-base-300 text-primary',
      'focus:ring-primary focus:ring-offset-0',
      'transition-colors cursor-pointer',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      sizeClasses[size],
      getFocusClasses(),
      className
    );

    return (
      <div className="flex items-start">
        <div className="flex items-center h-5">
          <input
            ref={(node) => {
              if (node && indeterminate !== undefined) {
                node.indeterminate = indeterminate;
              }
              if (typeof ref === 'function') {
                ref(node);
              } else if (ref) {
                ref.current = node;
              }
            }}
            id={checkboxId}
            type="checkbox"
            className={checkboxClasses}
            {...props}
          />
        </div>
        
        {(label || description) && (
          <div className="ml-3">
            {label && (
              <label
                htmlFor={checkboxId}
                className="text-sm font-medium text-base-content cursor-pointer"
              >
                {label}
              </label>
            )}
            {description && (
              <p className="text-sm text-base-content/60">
                {description}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

